import { useEffect, useRef, useState } from "react";
import { useReducedMotion } from "@/hooks/useReducedMotion";
import { TransparentPictureAsset } from "@/components/site/PictureAsset";
import "@/components/site/ProcessTable.css";

const TABLE_BASE = "/assets/homepage/process/table-vertical";

const SCENES = [
  {
    num: "01",
    eyebrow: "01 / The Story",
    heading: "We begin with the world inside.",
    body: "Habits, memories, keepsakes, and quiet moments become the first language of the home.",
  },
  {
    num: "02",
    eyebrow: "02 / The Space",
    heading: "We listen to what the space holds.",
    body: "Every quiet cue within and beyond its walls begins to reveal what it can become.",
  },
  {
    num: "03",
    eyebrow: "03 / The Composition",
    heading: ["We gather everything", "into flow."],
    body: "Each layer finds its place until the home feels whole.",
  },
];

// Heading renderer — supports an optional 2-line break that only
// engages on viewports wide enough to host both lines. On narrower
// screens the inline <br> collapses (display: none) and the text
// flows together so it wraps naturally without orphaning words.
const renderHeading = (h) => {
  if (!Array.isArray(h)) return h;
  return h.map((part, i) => (
    <span key={i}>
      {part}
      {i < h.length - 1 ? (
        <>
          {" "}
          <br className="process__heading-break" aria-hidden="true" />
        </>
      ) : null}
    </span>
  ));
};

const headingToText = (h) => (Array.isArray(h) ? h.join(" ") : h);

export default function ProcessTable({ id = "process-section" }) {
  const reducedMotion = useReducedMotion();
  const journeyRef = useRef(null);
  const imageInnerRef = useRef(null);
  const [activeIdx, setActiveIdx] = useState(0);
  const [progress, setProgress] = useState(0);
  const [isDesktop, setIsDesktop] = useState(
    typeof window !== "undefined"
      ? window.matchMedia("(min-width: 881px)").matches
      : true
  );

  // Track viewport
  useEffect(() => {
    const mq = window.matchMedia("(min-width: 881px)");
    const onChange = () => setIsDesktop(mq.matches);
    onChange();
    mq.addEventListener?.("change", onChange);
    return () => mq.removeEventListener?.("change", onChange);
  }, []);

  // Intro / outro fade reveals via IntersectionObserver
  useEffect(() => {
    const root = document.getElementById(id);
    if (!root) return;
    const targets = root.querySelectorAll("[data-process-reveal]");
    if (reducedMotion || !("IntersectionObserver" in window)) {
      targets.forEach((n) => n.classList.add("is-in"));
      return;
    }
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("is-in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.2, rootMargin: "0px 0px -8% 0px" }
    );
    targets.forEach((n) => io.observe(n));
    return () => io.disconnect();
  }, [id, reducedMotion]);

  // Pinned journey — scroll progress drives image translate + active scene.
  // Runs on BOTH desktop and mobile (only reduced motion falls back to the
  // stacked rendering). Mobile uses the same progress / activeIdx state;
  // the layout is reshaped via CSS so the image fills the sticky stage
  // and the active stage caption sits over a soft dark gradient at the
  // bottom of the viewport.
  useEffect(() => {
    if (reducedMotion) return;
    const el = journeyRef.current;
    if (!el) return;

    let rafId = null;
    const update = () => {
      rafId = null;
      const rect = el.getBoundingClientRect();
      const vh = window.innerHeight;
      const total = Math.max(1, rect.height - vh);
      const scrolled = Math.max(0, -rect.top);
      const p = Math.min(1, Math.max(0, scrolled / total));
      setProgress(p);
      // 3 beats — equal thirds. Use a slight bias toward edges so the
      // last/first scene linger.
      const N = SCENES.length;
      const idx = Math.min(N - 1, Math.floor(p * N));
      setActiveIdx(idx);
    };
    const onScroll = () => {
      if (rafId != null) return;
      rafId = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", onScroll, { passive: true });
    window.addEventListener("resize", onScroll);
    return () => {
      window.removeEventListener("scroll", onScroll);
      window.removeEventListener("resize", onScroll);
      if (rafId != null) cancelAnimationFrame(rafId);
    };
  }, [reducedMotion]);

  // Apply transform to image inner. The image is sized by WIDTH (with
  // height: auto preserving its native aspect ratio) and renders taller
  // than the viewport on every breakpoint, so we walk the table through
  // a fixed window:
  //   progress 0 → top of table (Story) sits in view
  //   progress 1 → bottom of table (Composition + lamp) sits in view
  // Travel is computed from the real rendered image height so we never
  // distort, squeeze, or clip the chair / lamp / crumpled paper.
  useEffect(() => {
    const inner = imageInnerRef.current;
    if (!inner) return;
    if (reducedMotion) {
      inner.style.transform = "translateX(-50%)";
      return;
    }
    const apply = () => {
      const vh = window.innerHeight;
      const imgH = inner.offsetHeight;
      // Image always taller than viewport. Walk it from "top aligned with
      // viewport top" to "bottom aligned with viewport bottom" — that
      // guarantees the lamp + crumpled paper at the very bottom are in
      // view at progress 1, and the chair area at the top is in view at
      // progress 0.
      const startY = 0;
      const endY = Math.min(0, vh - imgH);
      const offset = startY + (endY - startY) * progress;
      inner.style.transform = `translate3d(-50%, ${offset}px, 0)`;
    };
    apply();
    const img = inner.querySelector("img");
    if (img && !img.complete) {
      img.addEventListener("load", apply, { once: true });
      return () => img.removeEventListener("load", apply);
    }
  }, [progress, reducedMotion]);

  const pinned = !reducedMotion;

  return (
    <section
      id={id}
      className="process"
      data-testid="process-section"
      aria-label="Our process — how a home is composed"
    >
      <div className="process__paper" aria-hidden="true" />

      {/* INTRO */}
      <div
        className="process__intro"
        data-process-reveal
        data-testid="process-intro"
      >
        <span className="process__eyebrow">Our Process</span>
        <h2 className="process__headline">The Making</h2>
        <p className="process__subtext">
          We begin by understanding the people, reading the space, and shaping
          both into a home of belonging.
        </p>
      </div>

      {/* PINNED TABLE JOURNEY — desktop + mobile.
          Only reduced motion falls back to the stacked rendering below. */}
      {pinned ? (
        <div
          ref={journeyRef}
          className="process__journey"
          style={{ height: `${(SCENES.length + 0.5) * 100}vh` }}
          data-testid="process-journey"
        >
          <div className="process__sticky">
            <div className="process__bg-glow" aria-hidden="true" />
            <div className="process__inner">
              {/* TEXT COLUMN — desktop only. The mobile caption lives
                  in its own absolutely-positioned layer below so the
                  table image can fill the entire sticky stage. */}
              {isDesktop && (
                <div className="process__text-col">
                  <div className="process__progress" aria-hidden="true">
                    <span className="process__progress-line" />
                    <span
                      className="process__progress-tick"
                      style={{ top: `${progress * 100}%` }}
                    />
                    {SCENES.map((s, i) => (
                      <span
                        key={s.num}
                        className={[
                          "process__progress-num",
                          i === activeIdx ? "is-active" : "",
                        ].join(" ")}
                        style={{ top: `${(i / (SCENES.length - 1)) * 100}%` }}
                      >
                        {s.num}
                      </span>
                    ))}
                  </div>

                  <div className="process__scenes" aria-live="polite">
                    {SCENES.map((s, i) => (
                      <article
                        key={s.num}
                        className={[
                          "process__scene",
                          i === activeIdx ? "is-active" : "",
                        ].join(" ")}
                        aria-hidden={i !== activeIdx}
                        data-testid={`process-scene-${s.num}`}
                      >
                        <span className="process__scene-eyebrow">{s.eyebrow}</span>
                        <h3 className="process__scene-heading">{renderHeading(s.heading)}</h3>
                        <p className="process__scene-body">{s.body}</p>
                      </article>
                    ))}
                  </div>
                </div>
              )}

              {/* IMAGE — table that journeys upward. On desktop it sits
                  in the right column of the grid; on mobile it fills the
                  entire sticky stage (see ProcessTable.css mobile block). */}
              <div
                className="process__image-wrap"
                data-testid="process-image-wrap"
              >
                <div ref={imageInnerRef} className="process__image-inner">
                  <span
                    className="process__floor-shadow"
                    aria-hidden="true"
                  />
                  <span
                    className="process__chair-shadow"
                    aria-hidden="true"
                  />
                  <span
                    className="process__paper-shadow"
                    aria-hidden="true"
                  />
                  <TransparentPictureAsset
                    base={TABLE_BASE}
                    alt="A working table seen from above — sketches, leather notebook, materials, lamp and crumpled paper"
                    className="process__image"
                    loading="eager"
                    decoding="async"
                    fetchpriority="high"
                  />
                </div>
              </div>
            </div>

            {/* MOBILE OVERLAY — soft dark gradient + active stage caption
                pinned in the lower third of the sticky stage. Rendered
                only on mobile; hidden on desktop via CSS regardless. */}
            {!isDesktop && (
              <>
                <div className="process__mobile-veil" aria-hidden="true" />
                <div
                  className="process__mobile-stage"
                  aria-live="polite"
                  data-testid="process-mobile-stage"
                >
                  {SCENES.map((s, i) => (
                    <article
                      key={s.num}
                      className={[
                        "process__scene",
                        "process__scene--mobile",
                        i === activeIdx ? "is-active" : "",
                      ].join(" ")}
                      aria-hidden={i !== activeIdx}
                      data-testid={`process-scene-mobile-${s.num}`}
                    >
                      <span className="process__scene-eyebrow">{s.eyebrow}</span>
                      <h3 className="process__scene-heading">{renderHeading(s.heading)}</h3>
                      <p className="process__scene-body">{s.body}</p>
                    </article>
                  ))}
                </div>
              </>
            )}
          </div>
        </div>
      ) : (
        // REDUCED-MOTION FALLBACK — stacked image/text scenes
        <div className="process__stacked" data-testid="process-stacked">
          {SCENES.map((s, i) => (
            <article
              key={s.num}
              className="process__stacked-scene"
              data-process-reveal
            >
              <div className="process__stacked-image">
                <TransparentPictureAsset
                  base={TABLE_BASE}
                  alt={`Process scene ${s.num} — ${headingToText(s.heading)}`}
                  loading="lazy"
                  style={{
                    objectPosition: `center ${(i / (SCENES.length - 1)) * 100}%`,
                  }}
                />
              </div>
              <div className="process__stacked-text">
                <span className="process__scene-eyebrow">{s.eyebrow}</span>
                <h3 className="process__scene-heading">{renderHeading(s.heading)}</h3>
                <p className="process__scene-body">{s.body}</p>
              </div>
            </article>
          ))}
        </div>
      )}

      {/* OUTRO removed — the final CTA now lives at the end of the
          WE SHAPE section that follows. */}
    </section>
  );
}
